/**
 * TB Supporter — Corporate dashboard.
 * NON-FINANCIAL: workplace TB awareness, employee education, volunteer network,
 * community campaigns. NO CSR budget / spend / payment / monetary metrics —
 * activity indicators only. In-page section nav; real data; honest empty states.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard, Building2, GraduationCap, Megaphone, HandHeart, Library,
  MessagesSquare, Activity, Users, Bell, Bot, User, ArrowRight, BookMarked,
  Building, MapPin, ShieldCheck, Briefcase, BookOpen,
} from 'lucide-react'
import { GovLayout, GovPageTitle, GovCard, GovButton } from '@/components/layout/GovLayout'
import { FloatingTbAssistant } from '@/components/portal/FloatingTbAssistant'
import { SupporterRoleNav, type SupporterNavSection } from '@/components/supporter/SupporterRoleNav'
import {
  SupporterStatCard, SupporterSectionCard, SupporterResourceCard,
  SupporterCampaignCard, SupporterEmptyState,
} from '@/components/supporter/SupporterCards'
import { SupporterProfilePanel } from '@/components/supporter/SupporterProfilePanel'
import { SupporterCollaborationForm } from '@/components/supporter/SupporterCollaborationForm'
import { useAuthStore } from '@/stores/authStore'
import notificationService, { type AppNotification } from '@/services/notificationService'
import {
  saveResource, unsaveResource, getSavedResources,
  type Resource, type SavedResourceItem,
} from '@/services/resourceService'
import campaignService, { type Campaign } from '@/services/campaignService'
import { getMySupporterProfile } from '@/services/supporterProfileService'
import {
  useSupporterActivity, fetchActiveCampaigns, fetchResources,
  shareLink, campaignShareUrl, resourceShareUrl,
} from './supporterData'
import {
  AWARENESS_CAMPAIGN_TYPES, MEDICAL_DISCLAIMER, IMPACT_DISCLAIMER,
  COLLABORATION_OPTIONS,
} from './supporterContent'

const SECTIONS: SupporterNavSection[] = [
  { key: 'overview', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'company', label: 'Company Profile', icon: Building2 },
  { key: 'employee', label: 'Employee Awareness', icon: GraduationCap },
  { key: 'campaigns', label: 'Campaigns', icon: Megaphone },
  { key: 'volunteer', label: 'Volunteer Network', icon: HandHeart },
  { key: 'resources', label: 'TB Resources', icon: Library },
  { key: 'collaboration', label: 'Collaboration', icon: MessagesSquare },
  { key: 'impact', label: 'Impact', icon: Activity },
  { key: 'community', label: 'Community', icon: Users },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'assistant', label: 'TB Assistant', icon: Bot },
  { key: 'profile', label: 'My Profile', icon: User },
]

const CorporateSupporterDashboard = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const activity = useSupporterActivity()

  const [active, setActive] = useState('overview')
  const [orgName, setOrgName] = useState<string | null>(null)
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [resources, setResources] = useState<Resource[]>([])
  const [saved, setSaved] = useState<SavedResourceItem[]>([])
  const [savedIds, setSavedIds] = useState<Set<number>>(new Set())
  const [joinedIds, setJoinedIds] = useState<Set<number>>(new Set())
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [loadingList, setLoadingList] = useState(true)

  const displayName = orgName || user?.fullName || (user ? `${user.firstName} ${user.lastName}`.trim() : '') || t('supporter.hero.friend', 'friend')

  useEffect(() => {
    let active0 = true
    getMySupporterProfile().then((p) => { if (active0 && p.organizationName) setOrgName(p.organizationName) }).catch(() => {})
    setLoadingList(true)
    Promise.allSettled([
      fetchActiveCampaigns(AWARENESS_CAMPAIGN_TYPES, 12),
      fetchResources({ audience: 'CSR', size: 8 }),
      campaignService.getMyCampaigns({ size: 50 }),
      fetchSaved(),
    ]).then(([c, r, mine]) => {
      if (!active0) return
      setCampaigns(c.status === 'fulfilled' ? c.value : [])
      setResources(r.status === 'fulfilled' ? r.value : [])
      if (mine.status === 'fulfilled') setJoinedIds(new Set((mine.value.content ?? []).map((cm) => cm.id)))
      setLoadingList(false)
    })
    return () => { active0 = false }
  }, [])

  const fetchSaved = useCallback(async () => {
    try {
      const page = await getSavedResources(0, 50)
      const items = page.content ?? []
      setSaved(items)
      setSavedIds(new Set(items.map((i) => i.id)))
    } catch {
      setSaved([])
    }
  }, [])

  useEffect(() => {
    if (active !== 'notifications') return
    notificationService.getNotifications(0, 20).then((p) => setNotifications(p.content ?? [])).catch(() => setNotifications([]))
  }, [active])

  const toggleSave = useCallback(async (r: Resource) => {
    setSavedIds((prev) => { const next = new Set(prev); if (next.has(r.id)) next.delete(r.id); else next.add(r.id); return next })
    try {
      if (savedIds.has(r.id)) await unsaveResource(r.id)
      else await saveResource(r.id)
      await fetchSaved()
    } catch { await fetchSaved() }
  }, [savedIds, fetchSaved])

  const joinCampaign = useCallback(async (c: Campaign) => {
    setJoinedIds((prev) => new Set(prev).add(c.id))
    try { await campaignService.participate(c.id) } catch { setJoinedIds((prev) => { const n = new Set(prev); n.delete(c.id); return n }) }
  }, [])

  const onShareCampaign = useCallback((c: Campaign) => { shareLink(campaignShareUrl(c), c.title) }, [])
  const onShareResource = useCallback((r: Resource) => { shareLink(resourceShareUrl(r), r.title) }, [])

  // Impact tiles — awareness indicators only, NEVER monetary.
  const impactTiles = useMemo(() => [
    { icon: GraduationCap, value: 0, labelKey: 'supporter.corporate.impact.employeesEngaged', label: 'Employees Engaged', dataRequired: true, accent: 'bg-blue-50 text-blue-600' },
    { icon: Megaphone, value: 0, labelKey: 'supporter.corporate.impact.awarenessSessions', label: 'Awareness Sessions', dataRequired: true, accent: 'bg-emerald-50 text-emerald-600' },
    { icon: Megaphone, value: activity.campaignsJoined, labelKey: 'supporter.corporate.impact.campaignsJoined', label: 'Campaigns Joined', accent: 'bg-cyan-50 text-cyan-600' },
    { icon: HandHeart, value: 0, labelKey: 'supporter.corporate.impact.volunteers', label: 'Volunteers Participating', dataRequired: true, accent: 'bg-violet-50 text-violet-600' },
    { icon: BookMarked, value: activity.resourcesSaved, labelKey: 'supporter.corporate.impact.resourcesShared', label: 'Resources Saved', accent: 'bg-amber-50 text-amber-600' },
    { icon: Users, value: 0, labelKey: 'supporter.corporate.impact.communitiesReached', label: 'Communities Reached', dataRequired: true, accent: 'bg-rose-50 text-rose-600' },
  ], [activity])

  const primaryActions = [
    { label: t('supporter.corporate.actionEmployee', 'Start Employee Awareness'), icon: GraduationCap, onClick: () => setActive('employee') },
    { label: t('supporter.corporate.actionCampaign', 'Join a Campaign'), icon: Megaphone, onClick: () => setActive('campaigns') },
    { label: t('supporter.corporate.actionVolunteer', 'Engage Volunteers'), icon: HandHeart, onClick: () => setActive('volunteer') },
    { label: t('supporter.corporate.actionResources', 'Workplace TB Resources'), icon: Library, onClick: () => setActive('resources') },
  ]

  return (
    <GovLayout>
      <FloatingTbAssistant />
      <div className="space-y-5">
        <GovPageTitle
          title={t('supporter.corporate.title', 'Welcome, {{name}}', { name: displayName })}
          subtitle={t('supporter.corporate.subtitle', 'Build a TB-aware workplace and community.')}
          icon={Building2}
        />

        <GovCard>
          <div className="flex flex-wrap items-center gap-2.5">
            <GovButton variant="primary" size="sm" onClick={() => navigate('/assistant')} className="inline-flex items-center gap-1.5"><Bot className="w-4 h-4" /> {t('supporter.hero.tbAssistant', 'TB Assistant')}</GovButton>
            <GovButton variant="secondary" size="sm" onClick={() => setActive('notifications')} className="inline-flex items-center gap-1.5"><Bell className="w-4 h-4" /> {t('supporter.hero.notifications', 'Notifications')}
              {activity.unreadCount > 0 && <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-red-500 text-white">{activity.unreadCount}</span>}
            </GovButton>
            <GovButton variant="secondary" size="sm" onClick={() => navigate(`/profile/${user?.username}`)} className="inline-flex items-center gap-1.5"><User className="w-4 h-4" /> {t('supporter.hero.profile', 'Profile')}</GovButton>
            <span className="text-xs text-gray-400 ml-auto">{t('supporter.corporate.nonFinancial', 'Awareness & education only — no CSR budget or payments.')}</span>
          </div>
        </GovCard>

        <SupporterRoleNav sections={SECTIONS} active={active} onChange={setActive} />

        {/* Overview */}
        {active === 'overview' && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">{impactTiles.slice(0, 3).map((s) => <SupporterStatCard key={s.labelKey} {...s} />)}</div>
            <SupporterSectionCard title={t('supporter.corporate.primaryActions', 'What your organisation can do today')} icon={LayoutDashboard}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {primaryActions.map((a) => {
                  const Icon = a.icon
                  return (
                    <button key={a.label} onClick={a.onClick} className="flex items-center gap-3 p-4 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors text-left min-h-[56px]">
                      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-blue-600" /></div>
                      <span className="font-medium text-gray-900 text-sm flex-1 min-w-0">{a.label}</span>
                      <ArrowRight className="w-4 h-4 text-blue-500 shrink-0" />
                    </button>
                  )
                })}
              </div>
            </SupporterSectionCard>
            <div className="grid lg:grid-cols-2 gap-4">
              <SupporterSectionCard title={t('supporter.corporate.workplaceResources', 'Workplace awareness resources')} icon={Library} action={<GovButton variant="ghost" size="sm" onClick={() => setActive('resources')}>{t('supporter.actions.viewAll', 'View all')}</GovButton>}>
                {loadingList ? <SkeletonRows /> : resources.length === 0
                  ? <SupporterEmptyState icon="🏢" title={t('supporter.corporate.emptyResources', 'Workplace resources coming soon')} actionLabel={t('supporter.corporate.actionResources', 'Browse Resources')} onAction={() => setActive('resources')} />
                  : <div className="space-y-3">{resources.slice(0, 3).map((r) => <SupporterResourceCard key={r.id} resource={r} saved={savedIds.has(r.id)} onToggleSave={toggleSave} onShare={onShareResource} />)}</div>}
              </SupporterSectionCard>
              <SupporterSectionCard title={t('supporter.corporate.activeCampaigns', 'Active campaigns')} icon={Megaphone} action={<GovButton variant="ghost" size="sm" onClick={() => setActive('campaigns')}>{t('supporter.actions.viewAll', 'View all')}</GovButton>}>
                {loadingList ? <SkeletonRows /> : campaigns.length === 0
                  ? <SupporterEmptyState icon="📣" title={t('supporter.corporate.emptyCampaigns', 'No active campaigns right now')} actionLabel={t('supporter.corporate.actionCampaign', 'Explore Campaigns')} onAction={() => setActive('campaigns')} />
                  : <div className="space-y-3">{campaigns.slice(0, 3).map((c) => <SupporterCampaignCard key={c.id} campaign={c} joined={joinedIds.has(c.id)} onJoin={joinCampaign} onShare={onShareCampaign} onLearnMore={(cm) => navigate(`/campaigns/${cm.id}`)} />)}</div>}
              </SupporterSectionCard>
            </div>
          </div>
        )}

        {/* Company Profile */}
        {active === 'company' && (
          <SupporterSectionCard title={t('supporter.corporate.companyTitle', 'Company profile')} description={t('supporter.corporate.companyDesc', 'Organisation details help us recommend relevant awareness content.')} icon={Building2}>
            <div className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center shrink-0"><Building className="w-6 h-6 text-blue-600" /></div>
              <div className="min-w-0"><p className="font-semibold text-gray-900 truncate">{displayName}</p><p className="text-xs text-gray-500">{t('supporter.corporate.companyTag', 'Corporate TB Awareness Partner')}</p></div>
            </div>
            <SupporterProfilePanel role="corporate" />
          </SupporterSectionCard>
        )}

        {/* Employee Awareness */}
        {active === 'employee' && (
          <SupporterSectionCard title={t('supporter.corporate.employeeTitle', 'Employee awareness')} description={t('supporter.corporate.employeeDesc', 'Share TB awareness and stigma-reduction content with your workforce.')} icon={GraduationCap}>
            <div className="grid sm:grid-cols-2 gap-3">
              <EmployeeGuide icon={ShieldCheck} label={t('supporter.corporate.empStigma', 'Stigma reduction')} desc={t('supporter.corporate.empStigmaDesc', 'Foster a supportive, stigma-free workplace.')} to="/knowledge" />
              <EmployeeGuide icon={BookOpen} label={t('supporter.corporate.empBasics', 'TB basics for staff')} desc={t('supporter.corporate.empBasicsDesc', 'Symptoms, prevention and when to seek help.')} to="/knowledge" />
              <EmployeeGuide icon={MapPin} label={t('supporter.corporate.empCentre', 'Find TB services')} desc={t('supporter.corporate.empCentreDesc', 'Direct employees to official TB centres.')} to="/find-tb-centre" />
              <EmployeeGuide icon={Briefcase} label={t('supporter.corporate.empPolicy', 'Workplace support')} desc={t('supporter.corporate.empPolicyDesc', 'Support treatment adherence at work.')} to="/knowledge" />
            </div>
            <div className="mt-4"><GovButton variant="secondary" size="sm" onClick={() => setActive('resources')}>{t('supporter.corporate.actionResources', 'Workplace TB Resources')}<ArrowRight className="w-4 h-4 ml-1" /></GovButton></div>
          </SupporterSectionCard>
        )}

        {/* Campaigns */}
        {active === 'campaigns' && (
          <SupporterSectionCard title={t('supporter.corporate.campaignsTitle', 'Awareness campaigns')} description={t('supporter.corporate.campaignsDesc', 'Joining means awareness participation — no payment or sponsorship.')} icon={Megaphone}>
            {loadingList ? <SkeletonRows /> : campaigns.length === 0
              ? <SupporterEmptyState icon="📣" title={t('supporter.corporate.emptyCampaigns', 'No active campaigns right now')} actionLabel={t('supporter.corporate.actionCampaign', 'Explore Campaigns')} onAction={() => navigate('/campaigns')} />
              : <div className="grid sm:grid-cols-2 gap-3">{campaigns.map((c) => <SupporterCampaignCard key={c.id} campaign={c} joined={joinedIds.has(c.id)} onJoin={joinCampaign} onShare={onShareCampaign} onLearnMore={(cm) => navigate(`/campaigns/${cm.id}`)} />)}</div>}
          </SupporterSectionCard>
        )}

        {/* Volunteer Network */}
        {active === 'volunteer' && (
          <SupporterSectionCard title={t('supporter.corporate.volunteerTitle', 'Volunteer network')} description={t('supporter.corporate.volunteerDesc', 'Non-monetary employee volunteer awareness activities.')} icon={HandHeart}>
            {loadingList ? <SkeletonRows /> : campaigns.length === 0
              ? <SupporterEmptyState icon="🤝" title={t('supporter.corporate.emptyVolunteer', 'No volunteer opportunities right now')} />
              : <div className="grid sm:grid-cols-2 gap-3">{campaigns.slice(0, 6).map((c) => <SupporterCampaignCard key={c.id} campaign={c} joined={joinedIds.has(c.id)} onJoin={joinCampaign} onShare={onShareCampaign} onLearnMore={(cm) => navigate(`/campaigns/${cm.id}`)} />)}</div>}
            <div className="mt-4"><GovButton variant="secondary" size="sm" onClick={() => navigate('/volunteer')}>{t('supporter.corporate.exploreOpportunities', 'Explore opportunities')}<ArrowRight className="w-4 h-4 ml-1" /></GovButton></div>
          </SupporterSectionCard>
        )}

        {/* Resources */}
        {active === 'resources' && (
          <SupporterSectionCard title={t('supporter.corporate.resourcesTitle', 'Workplace TB resources')} description={t('supporter.corporate.resourcesDesc', 'Awareness and stigma-reduction materials for corporates.')} icon={Library}>
            {loadingList ? <SkeletonRows /> : resources.length === 0
              ? <SupporterEmptyState icon="🏢" title={t('supporter.corporate.emptyResources', 'Workplace resources coming soon')} actionLabel={t('supporter.corporate.actionResources', 'Browse Resources')} onAction={() => navigate('/resources')} />
              : <div className="grid sm:grid-cols-2 gap-3">{resources.map((r) => <SupporterResourceCard key={r.id} resource={r} saved={savedIds.has(r.id)} onToggleSave={toggleSave} onShare={onShareResource} />)}</div>}
            <div className="mt-4"><GovButton variant="secondary" size="sm" onClick={() => navigate('/resources')}>{t('supporter.corporate.browseResources', 'Browse all resources')}<ArrowRight className="w-4 h-4 ml-1" /></GovButton></div>
          </SupporterSectionCard>
        )}

        {/* Collaboration */}
        {active === 'collaboration' && (
          <SupporterSectionCard title={t('supporter.collaboration.heading', 'Collaboration')} description={t('supporter.collaboration.corporateDesc', 'Express interest in awareness, employee education or community campaigns. This is not a financial sponsorship.')} icon={MessagesSquare}>
            <SupporterCollaborationForm interestOptions={COLLABORATION_OPTIONS.corporate} />
          </SupporterSectionCard>
        )}

        {/* Impact */}
        {active === 'impact' && (
          <div className="space-y-5">
            <SupporterSectionCard title={t('supporter.corporate.impactTitle', 'Impact')} description={IMPACT_DISCLAIMER} icon={Activity}>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">{impactTiles.map((s) => <SupporterStatCard key={s.labelKey} {...s} />)}</div>
            </SupporterSectionCard>
            <SupporterSectionCard title={t('supporter.corporate.savedTitle', 'Saved resources')} icon={BookMarked}>
              {saved.length === 0
                ? <SupporterEmptyState icon="🔖" title={t('supporter.corporate.emptySaved', 'No saved resources yet')} actionLabel={t('supporter.corporate.actionResources', 'Browse Resources')} onAction={() => setActive('resources')} />
                : <div className="grid sm:grid-cols-2 gap-3">{saved.map((r) => <SupporterResourceCard key={r.id} resource={r} saved onToggleSave={toggleSave} onShare={onShareResource} />)}</div>}
            </SupporterSectionCard>
          </div>
        )}

        {/* Community */}
        {active === 'community' && (
          <SupporterSectionCard title={t('supporter.corporate.communityTitle', 'Community')} description={t('supporter.corporate.communityDesc', 'Join discussions, stories and awareness activities.')} icon={Users}>
            <div className="grid sm:grid-cols-2 gap-3">
              <CommunityLink icon={MessagesSquare} label={t('supporter.corporate.communityFeed', 'Community feed')} desc={t('supporter.corporate.communityFeedDesc', 'Discussions, posts and stories.')} to="/feed" />
              <CommunityLink icon={Megaphone} label={t('supporter.corporate.communityCampaigns', 'Awareness activities')} desc={t('supporter.corporate.communityCampaignsDesc', 'Join awareness campaigns.')} to="/campaigns" />
              <CommunityLink icon={BookOpen} label={t('supporter.corporate.communityKnowledge', 'Knowledge hub')} desc={t('supporter.corporate.communityKnowledgeDesc', 'Trusted TB articles.')} to="/knowledge" />
              <CommunityLink icon={Users} label={t('supporter.corporate.communityGroups', 'Groups')} desc={t('supporter.corporate.communityGroupsDesc', 'Connect with support groups.')} to="/groups" />
            </div>
          </SupporterSectionCard>
        )}

        {/* Notifications */}
        {active === 'notifications' && (
          <SupporterSectionCard title={t('supporter.corporate.notificationsTitle', 'Notifications')} icon={Bell} action={<GovButton variant="ghost" size="sm" onClick={() => notificationService.markAllAsRead().then(() => setNotifications((n) => n.map((x) => ({ ...x, isRead: true }))))}>{t('supporter.corporate.markAllRead', 'Mark all read')}</GovButton>}>
            {notifications.length === 0
              ? <SupporterEmptyState icon="🔔" title={t('supporter.corporate.emptyNotifications', 'You\'re all caught up')} description={t('supporter.corporate.emptyNotificationsDesc', 'No notifications right now.')} />
              : <div className="divide-y divide-gray-100">{notifications.map((n) => (
                  <div key={n.id} className={`py-3 ${n.isRead ? '' : 'bg-blue-50/40 -mx-2 px-2 rounded-lg'}`}>
                    <p className="text-sm font-medium text-gray-900">{n.title}</p>
                    <p className="text-sm text-gray-600">{n.message}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{new Date(n.createdAt).toLocaleString('en-IN')}</p>
                  </div>
                ))}</div>}
          </SupporterSectionCard>
        )}

        {/* Assistant */}
        {active === 'assistant' && (
          <SupporterSectionCard title={t('supporter.corporate.assistantTitle', 'TB Assistant')} description={MEDICAL_DISCLAIMER} icon={Bot}>
            <p className="text-sm text-gray-600">{t('supporter.corporate.assistantDesc', 'Discover TB awareness information, official resources and TB centres. The assistant does not replace a doctor.')}</p>
            <div className="mt-4 flex flex-wrap gap-2.5">
              <GovButton variant="primary" size="sm" onClick={() => navigate('/assistant')} className="inline-flex items-center gap-1.5"><Bot className="w-4 h-4" /> {t('supporter.corporate.openAssistant', 'Open TB Assistant')}</GovButton>
              <GovButton variant="secondary" size="sm" onClick={() => navigate('/find-tb-centre')} className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {t('supporter.corporate.findCentre', 'Find a TB Centre')}</GovButton>
            </div>
          </SupporterSectionCard>
        )}

        {/* Profile */}
        {active === 'profile' && (
          <SupporterSectionCard title={t('supporter.corporate.profileTitle', 'My profile')} description={t('supporter.corporate.profileDesc', 'Manage your details and awareness interests.')} icon={User}>
            <SupporterProfilePanel role="corporate" />
          </SupporterSectionCard>
        )}
      </div>
    </GovLayout>
  )
}

function SkeletonRows() {
  return <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />)}</div>
}

function CommunityLink({ icon: Icon, label, desc, to }: { icon: React.ComponentType<{ className?: string }>; label: string; desc: string; to: string }) {
  return (
    <Link to={to} className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors min-h-[56px]">
      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-blue-600" /></div>
      <div className="min-w-0 flex-1"><p className="font-medium text-gray-900 text-sm truncate">{label}</p><p className="text-xs text-gray-500 truncate">{desc}</p></div>
      <ArrowRight className="w-4 h-4 text-gray-400 shrink-0" />
    </Link>
  )
}

function EmployeeGuide({ icon: Icon, label, desc, to }: { icon: React.ComponentType<{ className?: string }>; label: string; desc: string; to: string }) {
  return (
    <Link to={to} className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 hover:bg-blue-50 transition-colors min-h-[56px]">
      <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-blue-600" /></div>
      <div className="min-w-0 flex-1"><p className="font-medium text-gray-900 text-sm truncate">{label}</p><p className="text-xs text-gray-500 truncate">{desc}</p></div>
      <ArrowRight className="w-4 h-4 text-gray-400 shrink-0" />
    </Link>
  )
}

export default CorporateSupporterDashboard